import { Badge, Banner, BlockStack, Box, Button, ButtonGroup, Card, InlineStack, Link, Text } from '@shopify/polaris';
import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '../../../context/useStore';
import { isAccountingRole } from '../../../utils/userRoles';
import { CardHeading } from '../cards/CardHeading';
import { formatDate, formatToCurrency } from '../functions';
import AllocationGrid from './AllocationGrid';
import { approvalsApi, DOC_TYPE_META, queueLink } from './approvalsApi';
import { describeStatus, labelFor, toneFor } from './approvalStatus';
import AssigneeModal from './AssigneeModal';
import PurchaseOrderPanel from './PurchaseOrderPanel';
import RejectModal from './RejectModal';
import { useAllocationDraft } from './useAllocationDraft';

/*
  Approval card on the invoice / credit note detail pages: status, assignee, the allocation grid and the actions the
  current user may take. Self-contained (fetches its own card payload) so the detail page save bar is not involved.
*/
export default function ApprovalPanel({ docType, id, onChanged, setToast, setErrorToast }) {
  const { user } = useAuthStore();
  const isAdmin = isAccountingRole(user?.role);
  const [card, setCard] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { type: 'assign'|'reassign'|'handoff'|'reject', lineIndex? }
  const [error, setError] = useState(null);
  const meta = DOC_TYPE_META[docType];
  const draft = useAllocationDraft(card, { currentUserId: user?.id, isAdmin, requireCostType: meta.hasCostType });

  const load = useCallback(async () => {
    if (!id || id === 'new') return;
    const response = await approvalsApi.card(docType, id);
    if (response.ok) setCard(response.data);
    else setError(response.error);
  }, [docType, id]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (action, { successToast = true } = {}) => {
    setBusy(true);
    setError(null);
    const response = await action();
    setBusy(false);
    if (response.ok) {
      setCard(response.data);
      if (successToast && setToast) setToast(true);
      if (onChanged) onChanged(response.data);
    } else {
      setError(response.error);
      if (setErrorToast) setErrorToast(true);
    }
    return response;
  };

  if (!id || id === 'new') return null;
  if (!card) {
    return (
      <Card>
        <CardHeading title={`${meta.label} approval`} />
        <Text tone="subdued">{error || 'Loading approval…'}</Text>
      </Card>
    );
  }

  const { document, permissions, allocation } = card;
  const status = document.ApprovalStatus;
  const rejected = card.lines.filter((line) => line.Status === 'REJECTED');
  const myLineIndexes = draft.lines.map((row, index) => (row.editable ? index : -1)).filter((i) => i >= 0);

  const saveAllocation = () => run(() => approvalsApi.saveAllocations(docType, id, draft.toPayload(), null));
  const approve = () => run(() => approvalsApi.approve(docType, id, { lines: draft.dirty ? draft.toPayload() : undefined }));

  return (
    <Card>
      <CardHeading title={`${meta.label} approval`} />
      <BlockStack gap="300">
        <Banner tone={toneFor(status) === 'success' ? 'success' : toneFor(status) === 'critical' ? 'critical' : toneFor(status) ? 'warning' : 'info'}>
          <InlineStack gap="300" blockAlign="center" wrap>
            <Badge tone={toneFor(status)}>{labelFor(status)}</Badge>
            <Text as="span">{describeStatus(status)}</Text>
            {document.assigneeAfkorting ? (
              <Text as="span">
                Assigned to <strong>{document.assigneeAfkorting}</strong>
              </Text>
            ) : null}
            {allocation.basis !== null ? (
              <Text as="span">
                Basis excl. VAT <strong>{formatToCurrency(allocation.basis)}</strong>
              </Text>
            ) : null}
            {status === 'PENDING' || status === 'REJECTED' ? (
              <Link url={queueLink(docType, id)} removeUnderline>
                Open in approval queue
              </Link>
            ) : null}
          </InlineStack>
          {rejected.length ? (
            <Box paddingBlockStart="200">
              {rejected.map((line) => (
                <Text as="p" key={line.id}>
                  Rejected by {line.decidedByAfkorting || line.approverAfkorting} {line.DecidedAt ? `on ${formatDate(line.DecidedAt)}` : ''}: <strong>{line.RejectReason}</strong>
                </Text>
              ))}
            </Box>
          ) : null}
        </Banner>

        <AllocationGrid
          draft={draft}
          docType={docType}
          canEdit={permissions.canEditAllocation}
          onHandOff={(index) => setModal({ type: 'handoff', lineIndex: index })}
          purchaseOrders={card.purchaseOrders}
          prefill={card.suggestions?.prefill}
          compactAllowed={false}
        />

        {error ? <Banner tone="critical">{error}</Banner> : null}

        <InlineStack align="space-between" blockAlign="center" wrap>
          <ButtonGroup>
            {permissions.canAssign && (status === 'TO_ASSIGN' || status === 'LEGACY') ? (
              <Button variant="primary" onClick={() => setModal({ type: 'assign', replace: status === 'LEGACY' })} disabled={busy}>
                Assign for approval
              </Button>
            ) : null}
            {permissions.canReopen && (status === 'REJECTED' || status === 'APPROVED' || status === 'LEGACY') ? (
              <Button onClick={() => run(() => approvalsApi.reopen(docType, id, {}))} disabled={busy}>
                {status === 'APPROVED' ? 'Undo approval (reopen)' : 'Reopen for approval'}
              </Button>
            ) : null}
            {permissions.canReassign ? (
              <Button onClick={() => setModal({ type: 'reassign' })} disabled={busy}>
                Reassign
              </Button>
            ) : null}
            {permissions.canAssign && status !== 'TO_ASSIGN' && status !== 'LEGACY' ? (
              <Button onClick={() => setModal({ type: 'assign', replace: true })} disabled={busy} variant="plain">
                Re-assign from scratch
              </Button>
            ) : null}
          </ButtonGroup>
          <ButtonGroup>
            {permissions.canEditAllocation && draft.dirty ? (
              <Button id="saveAllocation" onClick={saveAllocation} disabled={busy || draft.hasRowErrors} loading={busy}>
                Save split
              </Button>
            ) : null}
            {permissions.canReject ? (
              <Button tone="critical" onClick={() => setModal({ type: 'reject' })} disabled={busy}>
                Reject
              </Button>
            ) : null}
            {permissions.canApprove ? (
              <Button variant="primary" onClick={approve} disabled={busy || draft.hasRowErrors || (!draft.isBalanced && myLineIndexes.length === draft.lines.length)} loading={busy}>
                Approve{myLineIndexes.length < draft.lines.length ? ' my lines' : ''}
              </Button>
            ) : null}
          </ButtonGroup>
        </InlineStack>

        {meta.hasPurchaseOrders ? (
          <Box paddingBlockStart="200">
            <Text variant="headingSm" as="h3">
              Purchase orders
            </Text>
            <Box paddingBlockStart="200">
              <PurchaseOrderPanel
                card={card}
                canEdit={permissions.canLinkPurchaseOrder}
                busy={busy}
                onLink={(bestellingId) => run(() => approvalsApi.linkPurchaseOrder(id, bestellingId))}
                onUnlink={(bestellingId) => run(() => approvalsApi.unlinkPurchaseOrder(id, bestellingId))}
              />
            </Box>
          </Box>
        ) : null}
      </BlockStack>

      <AssigneeModal
        open={modal?.type === 'assign' || modal?.type === 'reassign' || modal?.type === 'handoff'}
        mode={modal?.type || 'reassign'}
        suggestions={card.suggestions?.assignees || []}
        defaultUserId={modal?.type === 'assign' ? card.suggestions?.suggestedAssigneeId || null : null}
        warning={
          modal?.type === 'assign' && modal?.replace && card.lines.length
            ? `Removes the current ${card.lines.length} allocation line${card.lines.length === 1 ? '' : 's'}, including approvals already given. The colleague you pick approves the full amount again.`
            : null
        }
        lines={draft.lines}
        defaultLineIds={modal?.type === 'handoff' && modal.lineIndex !== undefined ? [String(draft.lines[modal.lineIndex]?.id ?? draft.lines[modal.lineIndex]?._key)] : null}
        busy={busy}
        onClose={() => setModal(null)}
        onConfirm={async ({ toUserId, note, lineIds }) => {
          if (modal.type === 'assign') {
            const response = await run(() => approvalsApi.assign(docType, id, { assigneeId: toUserId, note, replace: Boolean(modal.replace) }));
            if (response.ok) setModal(null);
            return;
          }
          if (modal.type === 'handoff') {
            // hand-off is a draft change (approver on the row); it is sent to the server with "Save split" or "Approve"
            const target = (card.suggestions?.assignees || []).find((s) => s.userId === toUserId);
            draft.lines.forEach((row, index) => {
              if (lineIds.includes(String(row.id ?? row._key))) draft.update(index, { approverId: toUserId, approverAfkorting: target?.Afkorting || null });
            });
            setModal(null);
            return;
          }
          const response = await run(() => approvalsApi.reassign(docType, id, { toUserId, note }));
          if (response.ok) setModal(null);
        }}
      />
      <RejectModal
        open={modal?.type === 'reject'}
        busy={busy}
        onClose={() => setModal(null)}
        summary={`${meta.label} ${document[meta.numberField]} · ${document.supplierName || document.Leveranciernaam || ''} · ${formatToCurrency(allocation.basis)}`}
        onConfirm={async (reason) => {
          const response = await run(() => approvalsApi.reject(docType, id, reason));
          if (response.ok) setModal(null);
        }}
      />
    </Card>
  );
}
